import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Calculator, Target, BarChart3 } from 'lucide-react';

const faqs = [
  {
    q: '¿Cuál es el margen de beneficio normal de un restaurante?',
    a: 'El margen neto de un restaurante en España oscila entre el 3% y el 15% según el tipo de establecimiento. Restaurantes tradicionales: 3-8%. Bares y cafeterías: 10-15%. Fast food / QSR: 8-12%. Alta cocina: 5-10%. La clave es el control del prime cost (materia prima + personal).',
  },
  {
    q: '¿Cómo se calcula el margen bruto de un plato?',
    a: 'Margen bruto = PVP - Coste de ingredientes. Ejemplo: un plato que vendes a 18€ y cuesta 5€ de ingredientes tiene un margen bruto de 13€. El margen bruto porcentual sería: (13/18) × 100 = 72,2%.',
  },
  {
    q: '¿Qué es el prime cost en restauración?',
    a: 'El prime cost es la suma de tus dos costes principales: materia prima (food cost) + costes de personal. Un prime cost saludable no debería superar el 60% de la facturación. Si supera el 65%, la rentabilidad está en peligro.',
  },
  {
    q: '¿Cómo mejorar la rentabilidad de un restaurante sin subir precios?',
    a: 'Las 5 estrategias más efectivas: 1) Reducir mermas y desperdicio. 2) Negociar con proveedores o cambiar a productos con mejor relación calidad-precio. 3) Aplicar ingeniería de menú para destacar platos con mejor margen. 4) Optimizar porciones con fichas técnicas. 5) Reducir el desperdicio con mejor gestión de stock (FIFO).',
  },
  {
    q: '¿Cómo ayuda PLEKO a mejorar el margen?',
    a: 'PLEKO te da visibilidad total sobre el coste de cada plato en tiempo real. Detecta automáticamente cuando un plato supera el umbral de food cost, te alerta de subidas de precio de ingredientes, y te permite simular el impacto de cambios de precio o de proveedor sobre tu margen.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const benchmarks = [
  { tipo: 'Restaurante tradicional', margenNeto: '3-8%', primeCost: '55-65%', color: 'amber' },
  { tipo: 'Bar y cafetería', margenNeto: '10-15%', primeCost: '45-55%', color: 'green' },
  { tipo: 'Fast food / QSR', margenNeto: '8-12%', primeCost: '50-60%', color: 'green' },
  { tipo: 'Alta cocina', margenNeto: '5-10%', primeCost: '60-70%', color: 'amber' },
  { tipo: 'Catering y eventos', margenNeto: '8-15%', primeCost: '50-60%', color: 'green' },
  { tipo: 'Dark kitchen', margenNeto: '10-18%', primeCost: '45-55%', color: 'green' },
];

export default function MargenBeneficioPage() {
  return (
    <>
      <Helmet>
        <title>Margen de Beneficio en Restaurantes: Cómo Calcularlo y Mejorarlo | PLEKO</title>
        <meta
          name="description"
          content="Guía completa del margen de beneficio en restaurantes 2026. Benchmarks por tipo de negocio, fórmulas de cálculo, prime cost y estrategias para mejorar la rentabilidad."
        />
        <meta name="keywords" content="margen beneficio restaurante, rentabilidad restaurante, mejorar margen restaurante, prime cost hostelería, calcular margen plato, rentabilidad hostelería" />
        <link rel="canonical" href="https://pleko.app/margen-beneficio-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/margen-beneficio-restaurante" />
        <meta property="og:title" content="Margen de Beneficio en Restaurantes: Cómo Calcularlo y Mejorarlo | PLEKO" />
        <meta property="og:description" content="Guía completa del margen de beneficio en restaurantes 2026. Benchmarks por tipo de negocio, fórmulas de cálculo, prime cost y estrategias para mejorar la rentabilidad." />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Margen de Beneficio en Restaurantes: Cómo Calcularlo y Mejorarlo | PLEKO" />
        <meta name="twitter:description" content="Guía completa del margen de beneficio en restaurantes 2026. Benchmarks por tipo de negocio, fórmulas de cálculo, prime cost y estrategias para mejorar la rentabilidad." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-green-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
              <TrendingUp className="w-4 h-4" />
              Guía de rentabilidad 2026
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Margen de beneficio<br />
              <span className="text-green-500">en restaurantes</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              ¿Tu restaurante es rentable? Descubre los márgenes de referencia por tipo de negocio,
              cómo calcular tu prime cost y 5 estrategias para mejorar tu beneficio.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
            >
              Controlar mi rentabilidad con PLEKO
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Cuál es el margen normal de un restaurante?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La hostelería es un negocio de <strong>márgenes estrechos</strong>. Mientras que un comercio online puede tener márgenes del 30-50%, un restaurante opera típicamente con márgenes netos del 3% al 15%. Cada euro cuenta, y un pequeño descontrol en los costes puede convertir un mes rentable en pérdidas.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Benchmarks por tipo de establecimiento</h2>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de negocio</th>
                    <th className="p-3 text-center font-medium">Margen neto</th>
                    <th className="p-3 text-center font-medium">Prime cost objetivo</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarks.map(({ tipo, margenNeto, primeCost, color }, i) => (
                    <tr key={tipo} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className={`p-3 border-b border-slate-100 text-center font-bold ${color === 'green' ? 'text-green-600' : 'text-amber-600'}`}>{margenNeto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{primeCost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Las 3 fórmulas que debes conocer</h2>
            <div className="grid md:grid-cols-1 gap-4 mb-10">
              {[
                {
                  icon: Calculator,
                  title: 'Margen bruto por plato',
                  formula: 'Margen bruto = PVP − Coste ingredientes',
                  ejemplo: 'Plato a 18€ con coste 5€ → Margen = 13€ (72,2%)',
                },
                {
                  icon: Target,
                  title: 'Prime cost',
                  formula: 'Prime cost = (Materia prima + Personal) / Ventas × 100',
                  ejemplo: '15.000€ MP + 12.000€ personal / 45.000€ ventas → 60%',
                },
                {
                  icon: BarChart3,
                  title: 'Margen neto del restaurante',
                  formula: 'Margen neto = (Ventas − Todos los costes) / Ventas × 100',
                  ejemplo: '45.000€ − 41.000€ = 4.000€ → Margen neto 8,9%',
                },
              ].map(({ icon: Icon, title, formula, ejemplo }) => (
                <div key={title} className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-slate-900">{title}</h3>
                  </div>
                  <p className="text-lg font-semibold text-green-700 mb-1">{formula}</p>
                  <p className="text-slate-600 text-sm">Ejemplo: {ejemplo}</p>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">El prime cost: la métrica más importante</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>prime cost</strong> (materia prima + personal) suele representar el 55-65% de la facturación de un restaurante. Es la partida más grande y donde más margen de mejora hay.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-10 text-center">
              <p className="font-bold text-green-800 mb-2">Regla general del prime cost</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">&lt; 55%</p>
                  <p className="text-sm text-slate-600">Excelente</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-600">55-65%</p>
                  <p className="text-sm text-slate-600">Aceptable</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-600">&gt; 65%</p>
                  <p className="text-sm text-slate-600">Peligro</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">5 estrategias para mejorar tu margen</h2>
            <div className="space-y-4 mb-10">
              {[
                {
                  num: '1',
                  title: 'Controla el food cost en tiempo real',
                  desc: 'No puedes mejorar lo que no mides. Conocer el food cost exacto de cada plato es el primer paso. PLEKO lo calcula automáticamente con escandallos actualizados.',
                },
                {
                  num: '2',
                  title: 'Reduce mermas y desperdicios',
                  desc: 'El desperdicio alimentario representa en media el 15% de las compras en hostelería. Implementa FIFO, controla las porciones y aprovecha los recortes.',
                },
                {
                  num: '3',
                  title: 'Aplica ingeniería de menú',
                  desc: 'Destaca los platos más rentables, reformula los que tienen mal margen y elimina los que ni se venden ni son rentables.',
                },
                {
                  num: '4',
                  title: 'Negocia con proveedores con datos',
                  desc: 'Con un histórico de precios y consumos puedes negociar mejor. Compara precios entre proveedores y detecta subidas abusivas.',
                },
                {
                  num: '5',
                  title: 'Optimiza las porciones con fichas técnicas',
                  desc: 'Las fichas técnicas (escandallos) no solo sirven para calcular costes: aseguran que cada cocinero sirve la cantidad exacta, evitando variaciones.',
                },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre rentabilidad</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-center text-white">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Mejora tu margen con datos reales</h2>
            <p className="text-green-100 mb-6 max-w-xl mx-auto">
              PLEKO te muestra el food cost de cada plato, te alerta cuando baja la rentabilidad y te da las herramientas para tomar decisiones basadas en datos.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
            >
              Empezar gratis — 14 días sin tarjeta
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
